package com.iptvplayer.shared.data.remote.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ──────────────────────────────── Auth ────────────────────────────────

@Serializable
data class RegisterRequestDto(
    val username: String,
    val email: String,
    val password: String
)

@Serializable
data class LoginRequestDto(
    val username: String,
    val password: String
)

@Serializable
data class RefreshRequestDto(
    @SerialName("refresh_token") val refreshToken: String
)

@Serializable
data class AuthResponseDto(
    val user: UserDto,
    val tokens: TokenResponseDto
)

@Serializable
data class TokenResponseDto(
    @SerialName("access_token") val accessToken: String,
    @SerialName("refresh_token") val refreshToken: String,
    @SerialName("expires_at") val expiresAt: Long
)

@Serializable
data class UserDto(
    val id: String,
    val username: String,
    val email: String,
    @SerialName("created_at") val createdAt: Long,
    @SerialName("is_admin") val isAdmin: Boolean = false
)

// ──────────────────────────────── Source ────────────────────────────────

@Serializable
data class SourceDto(
    val id: String,
    val type: String,                 // "m3u" | "xtream"
    val name: String,
    val url: String? = null,
    @SerialName("server_url") val serverUrl: String? = null,
    val username: String? = null,
    val password: String? = null,
    @SerialName("created_at") val createdAt: Long = 0L
)

@Serializable
data class AddSourceRequestDto(
    val id: String,
    val type: String,
    val name: String,
    val url: String? = null,
    @SerialName("server_url") val serverUrl: String? = null,
    val username: String? = null,
    val password: String? = null
)

// ──────────────────────────────── Device ────────────────────────────────

@Serializable
data class RegisterDeviceRequestDto(
    val name: String,
    val platform: String
)

@Serializable
data class RenameDeviceRequestDto(
    val name: String
)

@Serializable
data class DeviceDto(
    val id: String,
    @SerialName("user_id") val userId: String,
    val name: String,
    val platform: String,
    @SerialName("is_online") val isOnline: Boolean = false,
    @SerialName("last_seen_at") val lastSeenAt: Long = 0L,
    @SerialName("registered_at") val registeredAt: Long = 0L
)

// ──────────────────────────────── Favorite ────────────────────────────────

@Serializable
data class AddFavoriteRequestDto(
    @SerialName("content_id") val contentId: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("content_name") val contentName: String,
    @SerialName("content_logo_url") val contentLogoUrl: String? = null,
    @SerialName("stream_url") val streamUrl: String
)

@Serializable
data class FavoriteDto(
    val id: String,
    @SerialName("user_id") val userId: String,
    @SerialName("content_id") val contentId: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("content_name") val contentName: String,
    @SerialName("content_logo_url") val contentLogoUrl: String? = null,
    @SerialName("stream_url") val streamUrl: String,
    @SerialName("added_at") val addedAt: Long = 0L
)

// ──────────────────────────────── Watch History ────────────────────────────────

@Serializable
data class UpdateHistoryRequestDto(
    @SerialName("content_id") val contentId: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("content_name") val contentName: String,
    @SerialName("content_logo_url") val contentLogoUrl: String? = null,
    @SerialName("stream_url") val streamUrl: String,
    @SerialName("resume_position_ms") val resumePositionMs: Long,
    @SerialName("duration_ms") val durationMs: Long? = null,
    @SerialName("series_source_id") val seriesSourceId: String? = null,
    @SerialName("series_id") val seriesId: Long? = null
)

@Serializable
data class WatchHistoryDto(
    val id: String,
    @SerialName("user_id") val userId: String,
    @SerialName("content_id") val contentId: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("content_name") val contentName: String,
    @SerialName("content_logo_url") val contentLogoUrl: String? = null,
    @SerialName("stream_url") val streamUrl: String,
    @SerialName("resume_position_ms") val resumePositionMs: Long = 0L,
    @SerialName("duration_ms") val durationMs: Long? = null,
    @SerialName("last_watched_at") val lastWatchedAt: Long = 0L,
    @SerialName("series_source_id") val seriesSourceId: String? = null,
    @SerialName("series_id") val seriesId: Long? = null
)

// ──────────────────────────────── Remote Command ────────────────────────────────

@Serializable
data class SendCommandRequestDto(
    @SerialName("target_device_id") val targetDeviceId: String,
    val action: String,
    @SerialName("content_id") val contentId: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("stream_url") val streamUrl: String,
    @SerialName("content_name") val contentName: String,
    @SerialName("resume_position_ms") val resumePositionMs: Long? = null
)

@Serializable
data class AckCommandRequestDto(
    @SerialName("command_id") val commandId: String,
    val status: String
)

@Serializable
data class RemoteCommandDto(
    val id: String,
    @SerialName("from_device_id") val fromDeviceId: String,
    @SerialName("target_device_id") val targetDeviceId: String,
    val action: String,
    @SerialName("content_id") val contentId: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("stream_url") val streamUrl: String,
    @SerialName("content_name") val contentName: String,
    @SerialName("resume_position_ms") val resumePositionMs: Long? = null,
    val status: String = "PENDING",
    @SerialName("created_at") val createdAt: Long = 0L
)

// ──────────────────────────────── Common Responses ────────────────────────────────

@Serializable
data class SuccessResponseDto(
    val success: Boolean = true,
    val message: String = ""
)

@Serializable
data class ErrorResponseDto(
    val error: String,
    val message: String = "",
    val status: Int = 0
)

// ──────────────────────────────── Admin ────────────────────────────────

@Serializable
data class SetDisabledRequestDto(val disabled: Boolean)

@Serializable
data class SetDeletionRequestedRequestDto(val requested: Boolean)

@Serializable
data class AdminUserDto(
    val id: String,
    val username: String,
    val email: String,
    @SerialName("created_at") val createdAt: Long = 0L,
    val disabled: Boolean = false,
    @SerialName("is_admin") val isAdmin: Boolean = false,
    @SerialName("deletion_requested") val deletionRequested: Boolean = false,
    @SerialName("deletion_requested_at") val deletionRequestedAt: Long? = null,
    @SerialName("source_count") val sourceCount: Int = 0,
    @SerialName("device_count") val deviceCount: Int = 0,
    @SerialName("favorite_count") val favoriteCount: Int = 0,
    @SerialName("history_count") val historyCount: Int = 0
)

@Serializable
data class AdminReportsDto(
    @SerialName("total_users") val totalUsers: Int = 0,
    @SerialName("disabled_users") val disabledUsers: Int = 0,
    @SerialName("pending_deletions") val pendingDeletions: Int = 0,
    @SerialName("new_users7d") val newUsers7d: Int = 0,
    @SerialName("total_sources") val totalSources: Int = 0,
    @SerialName("total_devices") val totalDevices: Int = 0,
    @SerialName("online_devices") val onlineDevices: Int = 0,
    @SerialName("total_favorites") val totalFavorites: Int = 0,
    @SerialName("total_history") val totalHistory: Int = 0
)

@Serializable
data class AdminUserDetailDto(
    val user: AdminUserDto,
    val sources: List<SourceDto> = emptyList(),
    val devices: List<DeviceDto> = emptyList(),
    val history: List<WatchHistoryDto> = emptyList(),
    val favorites: List<FavoriteDto> = emptyList()
)
